import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { notifications } from "@mantine/notifications";
import { IconX, IconCheck } from "@tabler/icons-react";
import CareerFormOverview from "./CareerFormOverview";
import CareerFormRequirement from "./CareerFormRequirement";
import CareerFormDescription from "./CareerFormDescription";
import Tab from "../atom/Tab";
import { z } from "zod";
import { API_BASE_URL } from "../../../config/api";

const tabs = ["Overview", "Requirements", "Description"];

/* --------------------------------- helpers -------------------------------- */
function htmlToText(html) {
  if (typeof window !== "undefined") {
    const div = document.createElement("div");
    div.innerHTML = html || "";
    return div.textContent?.replace(/\u200B/g, "").trim() || "";
  }
  return (html || "").replace(/<[^>]+>/g, "").replace(/\u200B/g, "").trim();
}

const nonEmptyRichText = z
  .string()
  .transform((html) => ({ html, text: htmlToText(html) }))
  .refine((v) => v.text.length > 0, { message: "This field is required" });

const formSchema = z.object({
  referenceNumber: z.string().min(1, "Reference Number is required"),
  region: z.string().min(1, "Region is required"),
  city: z.string().min(1, "City is required"),
  organisationName: z.string().min(1, "Organisation is required"),
  mtnLevel: z.string().min(1, "MTN Level is required"),
  applicationDeadline: z.coerce.date({
    required_error: "Application Deadline is required",
  }),
  experience: nonEmptyRichText,
  educationBackground: nonEmptyRichText,
  aboutMtn: nonEmptyRichText,
  mission: nonEmptyRichText,
  keyPerformanceAreas: nonEmptyRichText,
  applyInstructions: nonEmptyRichText,
});

function buildErrorMap(zodError) {
  const issues = Array.isArray(zodError?.issues)
    ? zodError.issues
    : Array.isArray(zodError?.errors)
    ? zodError.errors
    : [];
  const map = {};
  for (const i of issues) {
    const key = Array.isArray(i.path) ? i.path[0] : i.path;
    const msg = i?.message || "Invalid value";
    if (key && !map[key]) map[key] = msg;
  }
  if (!issues.length && zodError?.message && !map._root) {
    map._root = zodError.message;
  }
  return map;
}

const fieldLabels = {
  referenceNumber: "Reference Number",
  region: "Region",
  city: "City",
  organisationName: "Organisation Name",
  mtnLevel: "MTN Level",
  applicationDeadline: "Application Deadline",
  experience: "Experience",
  educationBackground: "Education Background",
  aboutMtn: "About MTN",
  mission: "Mission",
  keyPerformanceAreas: "Key Performance Areas",
  applyInstructions: "How To Apply",
};

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const safeDate = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const CareerFormTemplate = () => {
  const [activeTab, setActiveTab] = useState("Overview");
  const [overviewData, setOverviewData] = useState({});
  const [requirementData, setRequirementData] = useState({});
  const [descriptionData, setDescriptionData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const { requestId, jobId } = useParams();
  const isEditMode = Boolean(jobId);

  const navigate = useNavigate();
  const headers = useMemo(() => ({ headers: authHeaders() }), []);

  // CREATE prefill
  useEffect(() => {
    if (isEditMode) return;
    if (!requestId) return;

    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/requests/${requestId}`,
          headers
        );
        const r = res.data || {};

        setOverviewData((p) => ({
          ...p,
          referenceNumber: r.referenceNumber ?? r.requestId ?? p?.referenceNumber,
          region: r.region ?? p?.region,
          city: r.city ?? p?.city,
          organization: r.organisationName ?? r.department ?? p?.organization,
          level: r.mtnLevel ?? r.jobLevel ?? p?.level,
          deadline: safeDate(r.applicationDeadline) ?? p?.deadline,
        }));

        setRequirementData((p) => ({
          ...p,
          experience: r.experience ?? p?.experience,
          educationBackground: r.educationBackground ?? p?.educationBackground,
        }));

        setDescriptionData((p) => ({
          ...p,
          about: r.aboutMtn ?? r.about ?? p?.about,
          mission: r.mission ?? p?.mission,
          kpa: r.keyPerformanceAreas ?? r.kpa ?? p?.kpa,
          applyInstructions: r.applyInstructions ?? p?.applyInstructions,
        }));
      } catch (err) {
        console.error("Prefill error:", err.response?.data || err.message);
      }
    })();
  }, [requestId, headers, isEditMode]);

  // ✅ EDIT prefill (admin endpoint)
  useEffect(() => {
    if (!isEditMode) return;
    if (!jobId) return;

    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/jobs/admin/${jobId}`,
          headers
        );
        const j = res?.data?.data ?? null;
        if (!j) throw new Error("Job not found");

        setOverviewData({
          referenceNumber: j.referenceNumber ?? "",
          region: j.region ?? "",
          city: j.city ?? "",
          organization: j.organisationName ?? j.organization ?? "",
          level: j.mtnLevel ?? j.level ?? "",
          deadline: safeDate(j.applicationDeadline),
        });

        setRequirementData({
          experience: j.experience ?? "",
          educationBackground: j.educationBackground ?? "",
        });

        setDescriptionData({
          about: j.aboutMtn ?? j.about ?? "",
          mission: j.mission ?? "",
          kpa: j.keyPerformanceAreas ?? j.kpa ?? "",
          applyInstructions: j.applyInstructions ?? j.howToApply ?? "",
        });
      } catch (err) {
        console.error("Edit prefill error:", err.response?.data || err.message);
        notifications.show({
          title: "Failed to load job for editing",
          message: String(err?.response?.data?.message || err?.message || "Unknown error"),
          color: "red",
          icon: <IconX size={20} />,
          autoClose: false,
        });
        navigate("/candidate");
      }
    })();
  }, [isEditMode, jobId, headers, navigate]);

  const hasAnyInput = () => {
    const all = { ...overviewData, ...requirementData, ...descriptionData };
    return Object.values(all).some(
      (v) => v instanceof Date || (v != null && String(v).trim() !== "")
    );
  };

  const handleFinalSave = async () => {
    if (isSaving) return;

    if (!isEditMode && !requestId) {
      notifications.show({
        title: "Missing Request ID",
        message: "Cannot proceed without a request ID.",
        color: "red",
        icon: <IconX size={20} />,
        autoClose: false,
      });
      return;
    }

    const combined = {
      referenceNumber: overviewData.referenceNumber,
      region: overviewData.region,
      city: overviewData.city,
      organisationName: overviewData.organization,
      mtnLevel: overviewData.level,
      applicationDeadline: overviewData.deadline,
      experience: requirementData.experience ?? "",
      educationBackground: requirementData.educationBackground ?? "",
      aboutMtn: descriptionData.about ?? "",
      mission: descriptionData.mission ?? "",
      keyPerformanceAreas: descriptionData.kpa ?? "",
      applyInstructions: descriptionData.applyInstructions ?? "",
    };

    const parsed = formSchema.safeParse(combined);
    if (!parsed.success) {
      const map = buildErrorMap(parsed.error);
      setErrors(map);

      const msg =
        Object.entries(map)
          .filter(([k]) => k !== "_root")
          .map(([k, m]) => `• ${(fieldLabels[k] ?? k)}: ${m}`)
          .join("\n") ||
        map._root ||
        "Please fix the highlighted fields";

      notifications.show({
        title: "Fix the highlighted fields",
        message: msg,
        color: "red",
        icon: <IconX size={20} />,
        autoClose: false,
      });
      return;
    }

    setErrors({});
    const v = parsed.data;

    const payload = {
      ...(isEditMode ? {} : { requestId }),
      referenceNumber: v.referenceNumber,
      city: v.city,
      region: v.region,
      organisationName: v.organisationName,
      mtnLevel: v.mtnLevel,
      applicationDeadline: v.applicationDeadline.toISOString(),
      aboutMtn: v.aboutMtn.html,
      experience: v.experience.html,
      educationBackground: v.educationBackground.html,
      mission: v.mission.html,
      keyPerformanceAreas: v.keyPerformanceAreas.html,
      applyInstructions: v.applyInstructions.html,
      howToApply: v.applyInstructions.html,
    };

    try {
      setIsSaving(true);

      if (isEditMode) {
        await axios.patch(`${API_BASE_URL}/api/jobs/${jobId}`, payload, {
          headers: {
            ...authHeaders(),
            "Content-Type": "application/json",
          },
        });

        notifications.show({
          title: "Job Updated",
          message: "Job ad successfully updated.",
          color: "teal",
          icon: <IconCheck size={20} />,
          autoClose: 4000,
        });
      } else {
        const idempKey = `${requestId}:${payload.applicationDeadline}`;
        await axios.post(`${API_BASE_URL}/api/jobs`, payload, {
          headers: {
            ...authHeaders(),
            "Content-Type": "application/json",
            "Idempotency-Key": idempKey,
          },
        });

        notifications.show({
          title: "Job Created",
          message: "Job ad successfully created.",
          color: "teal",
          icon: <IconCheck size={20} />,
          autoClose: 4000,
        });
      }

      navigate("/candidate");
    } catch (err) {
      const status = err.response?.status;
      const serverMsg = err.response?.data;

      notifications.show({
        title: isEditMode ? "Failed to update job" : "Failed to create job",
        message: `Status: ${status ?? "N/A"}\nDetails: ${JSON.stringify(
          serverMsg ?? err.message
        )}`,
        color: "red",
        icon: <IconX size={20} />,
        autoClose: false,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full max-w-6xl p-4 mx-auto bg-white border rounded shadow-sm md:p-8 min-h-screen">
      <div className="flex flex-wrap gap-4 border-b">
        {tabs.map((tab) => (
          <Tab
            key={tab}
            label={tab}
            isActive={activeTab === tab}
            onClick={() => setActiveTab(tab)}
          />
        ))}
      </div>

      <div className="pt-4">
        {activeTab === "Overview" && (
          <CareerFormOverview data={overviewData} onChange={setOverviewData} errors={errors} />
        )}
        {activeTab === "Requirements" && (
          <CareerFormRequirement
            data={requirementData}
            onChange={setRequirementData}
            errors={errors}
          />
        )}
        {activeTab === "Description" && (
          <CareerFormDescription
            data={descriptionData}
            onChange={setDescriptionData}
            errors={errors}
          />
        )}
      </div>

      {hasAnyInput() && (
        <div className="flex justify-end gap-4 mt-6">
          <button
            onClick={handleFinalSave}
            disabled={isSaving}
            className="px-6 py-2 text-black font-MTNfont bg-[#FFCC00] rounded hover:bg-[#FFCC00]"
          >
            {isSaving ? "Saving..." : isEditMode ? "Update" : "Advertise"}
          </button>
        </div>
      )}

      <style>{`
        input:focus, select:focus, textarea:focus, [contenteditable="true"]:focus {
          outline: 2px solid #FFCC00;
          outline-offset: 0;
          box-shadow: 0 0 0 3px rgba(255, 204, 0, 0.35);
          border-color: #FFCC00 !important;
        }
      `}</style>
    </div>
  );
};

export default CareerFormTemplate;
